import { KeyboardEvent, useEffect, useState, FC, ElementType } from 'react';
import { css } from '@emotion/react';
import { saveAs } from 'file-saver';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { formatFileSize } from '@frontend/media-helpers';
import { PdfPreview } from '@frontend/messaging-media-lightbox';
import { IconButton, SpinningLoader, Text, useTooltip } from '@frontend/design-system';
import type { PreviewAssetKind, SendingMediaItem } from '../types';
import { getReadableFileExtension, canPreview, getAssetKind } from '../utils/file-utils';

interface PreviewFileProps {
  as?: ElementType;
  media: SendingMediaItem;
  maxWidth?: number | string;
  width?: number | string;
  removeImageTrackingId?: string;
  onDelete?: () => void;
  assetKind?: PreviewAssetKind;
  isRemovable?: boolean;
  className?: string;
  showDownloadButton?: boolean;
  downloadButtonTrackingId?: string;
}

export const PreviewFile: FC<PreviewFileProps> = ({
  media,
  maxWidth = 248,
  removeImageTrackingId,
  onDelete,
  assetKind,
  isRemovable = true,
  as = 'li',
  className,
  showDownloadButton = false,
  downloadButtonTrackingId,
}) => {
  const { t } = useTranslation('inbox');
  const { Tooltip, triggerProps, tooltipProps } = useTooltip({ placement: 'top' });
  const Component = as;

  // component state
  const [showPdf, setShowPdf] = useState<boolean>(false);

  // derived state
  const isUploading = media.uploading || !media.mediaObj;
  const hasError = media.hasError;
  const [fileSrc, setFileSrc] = useState<string>();
  const src = media.previewSrc || fileSrc;
  const name = media.file?.name || media.id;
  const fileExtension = media.file
    ? getReadableFileExtension(media.file?.type, src)
    : getAssetKind(media.mediaObj?.mimetype).toUpperCase();
  const fileSize = formatFileSize(media.file?.size);
  const canPreviewFile = canPreview(assetKind);

  const handlePreview = () => {
    if (!canPreviewFile) return;
    setShowPdf(true);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      handlePreview();
    }
  };

  useEffect(() => {
    if (!media.file) return;
    const reader = new FileReader();

    reader.onload = () => setFileSrc(reader.result as string);
    reader.readAsDataURL(media.file);

    return () => {
      setFileSrc(undefined);
    };
  }, [media.file]);

  if (!src) return null;

  return (
    <Component
      css={styles.container(maxWidth, isUploading, canPreviewFile)}
      tabIndex={canPreviewFile ? 0 : -1}
      onKeyDown={handleKeyDown}
    >
      {isUploading && (
        <div css={styles.uploadingOverlay}>
          <SpinningLoader />
        </div>
      )}
      {!isUploading && isRemovable && onDelete && (
        <IconButton
          css={styles.removeButton(hasError)}
          aria-label={
            hasError
              ? t('Issue uploading file {{name}}. Click to remove.', { name })
              : t('Remove file attachment {{name}}.', { name })
          }
          label={hasError ? t('Issue uploading file. Click to remove.') : t('Remove file attachment.')}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            !isUploading && onDelete?.();
          }}
          showLabelOnHover={hasError}
          size='xsmall'
          {...(removeImageTrackingId && { trackingId: removeImageTrackingId })}
        >
          {hasError ? <Icon name='alert' color='error' /> : <Icon size={16} name='x' css={styles.iconButtonIcon} />}
        </IconButton>
      )}

      <figure css={styles.figure} onClick={handlePreview} className={className} {...triggerProps}>
        <span css={styles.fileIconWrapper}>
          <Icon name='file' fill='white' size={48} css={styles.fileIcon} />
          <Text
            color={canPreviewFile ? 'primary' : 'default'}
            weight='bold'
            size='small'
            css={styles.fileExtensionText}
            as='figcaption'
          >
            {fileExtension}
          </Text>
        </span>
        <span css={styles.fileInfo}>
          <Text size='small' color={canPreviewFile ? 'primary' : 'default'} css={styles.fileNameText}>
            {name}
          </Text>
          {fileSize && (
            <Text size='small' color='subdued'>
              {fileSize}
            </Text>
          )}
        </span>
        <Tooltip {...tooltipProps}>
          <Text size='medium' color='white'>
            {name}
          </Text>
        </Tooltip>
      </figure>
      {showDownloadButton && (
        <IconButton
          css={styles.downloadButton}
          aria-label={t('Download {{name}}.', { name })}
          label={t('Download {{name}}.', { name })}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            saveAs(src, name);
          }}
          showLabelOnHover={hasError}
          size='xsmall'
          {...(downloadButtonTrackingId && { trackingId: downloadButtonTrackingId })}
        >
          {<Icon size={16} name='download' css={styles.iconButtonIcon} />}
        </IconButton>
      )}
      {showPdf && (
        <PdfPreview
          src={src}
          filename={name}
          show={showPdf}
          onDownload={() => saveAs(src, name)}
          onClose={() => setShowPdf(false)}
        />
      )}
    </Component>
  );
};

const styles = {
  container: (maxWidth: number | string, isUploading: boolean, canPreviewFile: boolean) =>
    css({
      listStyleType: 'none',
      height: '100%',
      maxWidth,
      width: '100%',
      flexShrink: 0,
      cursor: isUploading || !canPreviewFile ? 'auto' : 'pointer',
      position: 'relative',
      borderRadius: 'var(--fabric-radius-md)',
      backgroundColor: 'var(--fabric-color-neutral-alpha-100-alpha-5)',
      border: 'none',
      ...(canPreviewFile && {
        ':hover': {
          outline: `2px solid var(--fabric-color-blue-50)`,
        },
      }),
    }),

  uploadingOverlay: css({
    transition: 'background-color 300ms ease-in-out',
    zIndex: 1,
    maxHeight: '100%',
    height: 'auto',
    width: 'auto',
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    backgroundColor: `rgba(255, 255, 255, 0.5)`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }),

  removeButton: (hasError?: boolean) =>
    css({
      position: 'absolute',
      top: 'var(--fabric-space-gap-xs)',
      right: 'var(--fabric-space-gap-xs)',
      backgroundColor: hasError ? 'transparent' : 'var(--fabric-color-surface-default)',
      padding: 0,
    }),

  downloadButton: css({
    position: 'absolute',
    top: 'var(--fabric-space-gap-xs)',
    right: 'var(--fabric-space-gap-xs)',
    backgroundColor: 'var(--fabric-color-surface-default)',
    padding: 0,
  }),

  iconButtonIcon: css({ padding: 0 }),

  figure: css({
    height: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    color: 'var(--fabric-color-text-primary)',
    borderRadius: 'var(--fabric-radius-md)',
  }),

  fileIconWrapper: css({
    display: 'grid',
    placeItems: 'center',
  }),

  fileIcon: css({
    gridArea: '1 / 1',
    use: {
      color: 'white',
      boxShadow: `var(--fabric-card-shadow)`,
    },
  }),

  fileExtensionText: css({ gridArea: '1 / 1' }),

  fileInfo: css({
    gridArea: '1 / 2',
    display: 'flex',
    flexDirection: 'column',
    paddingTop: '8px',
    justifyContent: 'center',
    width: 'calc(100% - 50px)',
  }),

  fileNameText: css({
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    width: 'calc(100% - 40px)',
  }),
};
